/* eslint-disable react/jsx-boolean-value */
import React, { memo } from 'react'

import {
  number,
  string,
  bool,
  shape,
  oneOfType,
  arrayOf,
  object,
} from 'prop-types'

import { TopSectionPropsProvider } from 'Pods/ItemPage/TopSection/context/TopSectionPropsContext'
import { useTopSectionProps } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import {
  detailsAreaMockedData,
  detailsAreaPrintableMockedData,
} from 'Pods/Shared/ItemPageComponents/Mocks/detailsArea'

import DetailsArea from './Components/DetailsArea/DetailsArea'

function DetailsAreaWrapper({
  detailsAreaData,
  locale = 'en',
  extractingCoreStyles = false,
  showCgtVerification = false,
}) {
  const topSectionProps = useTopSectionProps()

  return extractingCoreStyles ? (
    <div className="extraction-versions-wrapper">
      <TopSectionPropsProvider
        value={{
          ...topSectionProps,
          is3dPrintModel: false,
        }}
      >
        <DetailsArea {...detailsAreaMockedData} locale={locale} showCgtVerification />
      </TopSectionPropsProvider>
      <TopSectionPropsProvider
        value={{
          ...topSectionProps,
          is3dPrintModel: true,
        }}
      >
        <DetailsArea {...detailsAreaPrintableMockedData} locale={locale} showCgtVerification />
      </TopSectionPropsProvider>
    </div>
  ) : (
    <DetailsArea
      {...detailsAreaData} locale={locale}
      showCgtVerification={showCgtVerification}
    />
  )
}

DetailsAreaWrapper.propTypes = {
  locale: string,
  detailsAreaData: shape({
    collections: arrayOf(shape({
      collectionPrice: number.isRequired,
      id: number.isRequired,
      itemsCount: number.isRequired,
      saleOffDiscount: number.isRequired,
      thumbnail: string.isRequired,
      title: string.isRequired,
      url: string.isRequired,
    })).isRequired,
    details: oneOfType([
      shape({
        animated: bool,
        rigged: bool,
        game_ready: bool,
        pbr: bool,
        textures: bool,
        materials: bool,
        uv_mapping: bool,
        plugins_used: bool,
      }),
      arrayOf(shape({
        title: string,
        value: oneOfType([string, number, bool]),
      })),
    ]),
    publishDate: string,
    id: number.isRequired,
    geometryType: oneOfType([string, object]),
    polygons: oneOfType([number, string]),
    vertices: oneOfType([number, string]),
    cgtVerificationData: shape({
      data: shape({
        files: shape({
          fbxFormat: shape({
            pass: bool,
          }),
        }),
        geometry: shape({
          facetedGeometry: shape({
            pass: bool,
          }),
          noNgons: shape({
            pass: bool,
          }),
          nonManifoldVertices: shape({
            pass: bool,
          }),
        }),
        naming: shape({
          allowedCharacters: shape({
            pass: bool,
          }),
        }),
        scene: shape({
          dccObjectTypes: shape({
            pass: bool,
          }),
        }),
        texturesAndMaterials: shape({
          embeddedTextures: shape({
            pass: bool,
          }),
          missingRequiredTextures: shape({
            pass: bool,
          }),
          numberOfMaterialsPerObject: shape({
            pass: bool,
          }),
          textureAspectRatio: shape({
            pass: bool,
          }),
          textureResolutionNotPowerOf2: shape({
            pass: bool,
          }),
        }),
        uV: shape({
          numberOfUVs: shape({
            pass: bool,
          }),
          overlappedUvPerUVIsland: shape({
            pass: bool,
          }),
        }),
      }),
      reportType: string,
      score: number,
    }),
    printModelVerificationData: shape({
      cleanGeometry: shape({
        manifoldGeometry: bool.isRequired,
        noSelfIntersections: bool.isRequired,
        noOverlappingFaces: bool.isRequired,
        noZeroAngledCorners: bool.isRequired,
        noZeroLengthEdges: bool.isRequired,
        noZeroAreaFaces: bool.isRequired,
        noBadContiguousEdges: bool.isRequired,
        noOpenEdges: bool.isRequired,
      }),
      score: number.isRequired,
    }),
    nativeFormats: arrayOf(shape({
      extensionPath: string,
      fileSize: string.isRequired,
      title: string.isRequired,
      version: string,
      rendererName: string,
      rendererVersion: oneOfType([string, number]),
    })),
    exchangeFormats: arrayOf(shape({
      extensionPath: string,
      fileSize: string.isRequired,
      title: string.isRequired,
      version: string,
      rendererName: string,
      rendererVersion: oneOfType([string, number]),
    })),
    ungroupedFormats: arrayOf(shape({
      extensionPath: string,
      fileSize: string.isRequired,
      title: string.isRequired,
      version: string,
      rendererName: string,
      rendererVersion: oneOfType([string, number]),
    })),
    units: string,
  }).isRequired,
  extractingCoreStyles: bool,
  showCgtVerification: bool,
}

export default memo(DetailsAreaWrapper)
